import {domToCanvas} from 'modern-screenshot';
import {isRenderableRect, type GenieRect} from './genie';

/** Elements carrying this attribute are excluded from the rasterised snapshot. */
export const SNAPSHOT_IGNORE_ATTRIBUTE = 'data-genie-ignore';

export interface GenieSnapshot {
  canvas: HTMLCanvasElement;
  rect: GenieRect;
}

export function rectFromDomRect(rect: DOMRect): GenieRect {
  return {height: rect.height, left: rect.left, top: rect.top, width: rect.width};
}

function nextFrame(view: Window) {
  return new Promise<void>((resolve) => {
    view.requestAnimationFrame(() => resolve());
  });
}

/** Keep only the `inset` layers of a box-shadow list. */
function insetOnlyBoxShadow(value: string) {
  if (!value || value === 'none') return 'none';
  const layers: string[] = [];
  let depth = 0;
  let start = 0;
  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    if (character === '(') depth += 1;
    else if (character === ')') depth = Math.max(0, depth - 1);
    else if (character === ',' && depth === 0) {
      const part = value.slice(start, index).trim();
      if (part) layers.push(part);
      start = index + 1;
    }
  }
  const tail = value.slice(start).trim();
  if (tail) layers.push(tail);

  const inset = layers.filter((layer) => /(?:^|\s)inset(?:\s|$)/i.test(layer));
  return inset.length ? inset.join(', ') : 'none';
}

/**
 * Rasterise a live DOM subtree into a canvas the genie animation can warp.
 *
 * Two details matter for the illusion:
 *
 *  - the OUTER box-shadow of the window is stripped (inset shadows are kept).
 *    A drop shadow baked into the bitmap would get stretched with the strips and
 *    smear into a grey haze as the window funnels down;
 *  - the clone is forced to `transform: none` at its natural size, so a window
 *    that is mid-animation still rasterises square.
 */
export async function captureGenieSnapshot(node: HTMLElement): Promise<GenieSnapshot | null> {
  const view = node.ownerDocument.defaultView;
  if (!view) return null;

  const rect = rectFromDomRect(node.getBoundingClientRect());
  if (!isRenderableRect(rect)) return null;

  // Let any pending style/layout settle before we read pixels.
  await nextFrame(view);

  const width = Math.max(1, Math.round(rect.width));
  const height = Math.max(1, Math.round(rect.height));

  const canvas = await domToCanvas(node, {
    backgroundColor: null,
    filter: (candidate: Node) =>
      candidate.nodeType !== 1 ||
      !(candidate as Element).hasAttribute(SNAPSHOT_IGNORE_ATTRIBUTE),
    features: {
      copyScrollbar: false,
      fixSvgXmlDecode: false,
      removeAbnormalAttributes: true,
      removeControlCharacter: true,
      restoreScrollPosition: true,
    },
    font: false,
    height,
    onCloneNode: (cloned: Node) => {
      if (cloned.nodeType !== 1) return;
      const element = cloned as HTMLElement;
      element.style.setProperty('box-shadow', insetOnlyBoxShadow(element.style.boxShadow), 'important');
      element.style.setProperty('contain', 'layout style', 'important');
      element.style.setProperty('height', `${height}px`, 'important');
      element.style.setProperty('width', `${width}px`, 'important');
      element.style.setProperty('transform', 'none', 'important');
    },
    scale: 1,
    style: {left: '0px', top: '0px', transform: 'none'},
    timeout: 800,
    width,
  });

  return {canvas, rect};
}
